import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type DocumentElement as DocumentElementType, isElementHidden } from '@eaco/forms-core';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Check, ChevronDown, ChevronLeft, ChevronRight } from '../ui/icons';
import { cn } from '../lib/utils';
import { usePreviewFormContext } from '../DocumentView/PreviewFormContext';
import { FillSubmitButton, FILL_ACTION_BAR_CLASS, FILL_ACTION_BAR_NARROW_CLASS, FILL_ACTION_BAR_ATTR } from '../DocumentView/FillSubmitButton';
import { useValidation } from '../DocumentView/ValidationContext';
import { useFormTheme } from '../DocumentView/FormThemeProvider';
import { useIsReadOnly, useIsPrint, useIsEditor, useResolvedStateMap } from '../DocumentView/DocumentContext';
import DocumentElement from '../DocumentView/DocumentElement';
import FillDesktopFrame from '../DocumentView/FillDesktopFrame';
import FormBrandHeader from '../DocumentView/FormBrandHeader';

interface Props {
  element: DocumentElementType;
}

// Themed accent (falls back to the default tint outside a FormThemeProvider,
// e.g. the editor design canvas). See @eaco/forms-core theme.
const TINT = 'var(--forms-accent,var(--forms-accent-default))';
// A very light tint of the accent for chips / highlighted rows.
const TINT_SOFT = 'color-mix(in srgb, var(--forms-accent,var(--forms-accent-default)) 12%, #fff)';

const getPageName = (page: DocumentElementType, index: number): string => {
  const name = (page.data as { name?: string })?.name;
  return name || `Page ${index + 1}`;
};

const Pages: React.FC<Props> = ({ element }) => {
  const isEditor = useIsEditor();
  const resolvedState = useResolvedStateMap();
  // Archived and hidden pages render only on the editor canvas (with their
  // banners); the dispatcher's guards never see a page because Pages renders
  // its children itself, so the filters live here. Hidden follows the fill
  // precedence (a fired rule beats the static flag).
  const pages = useMemo(
    () =>
      (element.elements || []).filter(
        (p) => isEditor || (!p.archived && !isElementHidden(p, resolvedState as Map<string, never>)),
      ),
    [element.elements, isEditor, resolvedState],
  );
  // Pages can drop out (a rule hides one), so the stored index is clamped
  // to the list actually rendered before anything reads it.
  const [rawPageIndex, setCurrentPageIndex] = useState(0);
  const currentPageIndex = Math.max(0, Math.min(rawPageIndex, pages.length - 1));
  const ctx = usePreviewFormContext();
  const { pageJumps, focusToken, incompleteElementIds } = useValidation();
  const isReadOnly = useIsReadOnly();
  const isPrint = useIsPrint();

  // Page indices that currently hold a required-but-empty field, so the
  // stepper dropdown can flag them. Recomputed live from the shared
  // incomplete-ids set (which already respects logic/visibility + the
  // required-override).
  const pagesWithGaps = useMemo(() => {
    const set = new Set<number>();
    if (!incompleteElementIds || incompleteElementIds.size === 0) return set;
    const hasGap = (els: DocumentElementType[] | undefined): boolean => {
      for (const el of els || []) {
        if (incompleteElementIds.has(el.id)) return true;
        if (el.elements && hasGap(el.elements)) return true;
      }
      return false;
    };
    pages.forEach((page, index) => {
      if (hasGap(page.elements)) set.add(index);
    });
    return set;
  }, [incompleteElementIds, pages]);

  // (The FillDesktopFrame resets its own scroll region on page change via
  // bodyScrollKey — the fill body scrolls internally, not the window.)

  // Each fresh submit-failure pass publishes a pageJumps map. If this
  // Pages element is listed, jump to the page that contains the
  // first failing field so the red bar is visible.
  useEffect(() => {
    const target = pageJumps.get(element.id);
    if (target === undefined) return;
    // validateDocument indexes into the unfiltered page list; translate
    // through the page's id so archived pages dropped above don't shift it.
    const rawPage = (element.elements || [])[target];
    const index = rawPage ? pages.findIndex((p) => p.id === rawPage.id) : -1;
    if (index >= 0 && index !== currentPageIndex) {
      setCurrentPageIndex(index);
    }
    // focusToken bumps on every failed submit so we re-react even
    // when the target page index didn't change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusToken, element.id]);

  if (pages.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-muted-foreground/25 p-6 text-center">
        <p className="text-sm italic text-muted-foreground">
          No pages added yet
        </p>
      </div>
    );
  }

  // PDF / print: the "pages" are only page breaks, not something to render.
  // Each page's content flows onto its own A4 sheet (a hard page break after
  // every page but the last); no on-screen "paper" card chrome or header — so
  // the output isn't a screenshot of the preview.
  if (isPrint) {
    return (
      <div>
        {pages.map((page, index) => {
          const pageChildren = page.elements || [];
          const isLastPage = index === pages.length - 1;
          return (
            <div
              key={page.id}
              className={cn('space-y-4', !isLastPage && 'break-after-page')}
            >
              {pageChildren.map((child) => (
                <DocumentElement key={child.id} element={child} />
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  // Viewer mode: each page renders as its own "paper" card so the
  // page metaphor is preserved when an output / submitted form is
  // being reviewed (Preview tab, Output dialog tab, PDF print page).
  if (isReadOnly) {
    return (
      <div className="space-y-10 py-2">
        {pages.map((page, index) => {
          const pageChildren = page.elements || [];
          return (
            <section
              key={page.id}
              className="overflow-hidden rounded-xl border border-border bg-white shadow-md ring-1 ring-black/5"
            >
              <header className="flex items-center justify-between gap-3 border-b border-border bg-muted/40 px-6 py-2.5">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {getPageName(page, index)}
                </h3>
                {pages.length > 1 && (
                  <span className="text-[11px] font-medium text-muted-foreground">
                    Page {index + 1} of {pages.length}
                  </span>
                )}
              </header>
              <div className="space-y-4 px-8 py-8">
                {pageChildren.length === 0 ? (
                  <p className="py-2 text-center text-sm italic text-muted-foreground">
                    No elements on this page
                  </p>
                ) : (
                  pageChildren.map((child) => (
                    <DocumentElement key={child.id} element={child} />
                  ))
                )}
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  const currentPage = pages[currentPageIndex];
  const pageChildren = currentPage?.elements || [];
  const isFirst = currentPageIndex === 0;
  const isLast = currentPageIndex === pages.length - 1;
  const multi = pages.length > 1;
  const submitting = !!ctx?.submitting;
  // The stepper's last-page button submits only when a submit host is wired
  // through context (Preview, Output preview, and the real fill — all provide
  // onSubmit). Without a host it stays navigation-only.
  const showStepperSubmit = isLast && !!ctx?.onSubmit;

  const handleNext = () => {
    if (isLast) {
      if (ctx?.onSubmit) void ctx.onSubmit();
      return;
    }
    setCurrentPageIndex((i) => Math.min(pages.length - 1, i + 1));
  };
  const handleBack = () => setCurrentPageIndex((i) => Math.max(0, i - 1));

  const prevName = !isFirst ? getPageName(pages[currentPageIndex - 1], currentPageIndex - 1) : '';
  const nextName = !isLast
    ? getPageName(pages[currentPageIndex + 1], currentPageIndex + 1)
    : '';
  const currentName = getPageName(currentPage, currentPageIndex);
  const showFooter = multi || showStepperSubmit;

  // The current page's fields — shared by both layouts (the desktop frame
  // holds them to a readable measure; the narrow layout stacks them).
  const body =
    pageChildren.length === 0 ? (
      <p className="py-4 text-center text-sm italic text-muted-foreground">
        No elements on this page
      </p>
    ) : (
      pageChildren.map((child) => (
        <DocumentElement key={child.id} element={child} />
      ))
    );

  // Prev / Next (or Submit) — identical buttons in both layouts; only the
  // surrounding bar padding differs (frame footer vs. sticky action bar).
  const footerButtons = showFooter ? (
    <>
      {!isFirst ? (
        <button
          type="button"
          onClick={handleBack}
          disabled={submitting}
          className="inline-flex max-w-[46%] items-center gap-[7px] overflow-hidden rounded-[9px] border border-[#dfe3e9] bg-white px-[12px] py-[9px] text-[12.5px] font-bold text-[#5a6472] disabled:opacity-50"
        >
          <ChevronLeft className="h-[14px] w-[14px] shrink-0" />
          <span className="truncate">{prevName}</span>
        </button>
      ) : (
        <div />
      )}
      <div className="flex-1" />
      {!isLast ? (
        <button
          type="button"
          onClick={handleNext}
          className="inline-flex max-w-[52%] items-center gap-[7px] overflow-hidden rounded-[9px] px-[14px] py-[9px] text-[12.5px] font-bold text-white shadow-[0_2px_6px_rgba(47,111,219,.3)] disabled:opacity-60"
          style={{ backgroundColor: TINT }}
        >
          <span className="truncate">{nextName}</span>
          <ChevronRight className="h-[14px] w-[14px] shrink-0" />
        </button>
      ) : showStepperSubmit ? (
        // The host owns the final Submit control on public fills (Turnstile
        // popover, "Protected by Cloudflare" line). Fall back to the plain
        // Submit button in preview / editor contexts.
        (ctx?.submitSlot ?? (
          <FillSubmitButton
            onClick={handleNext}
            submitting={submitting}
            label={ctx?.submitLabel}
            className="max-w-[52%]"
          />
        ))
      ) : null}
    </>
  ) : null;

  // Progress = pages genuinely done (visited, no remaining required gap) ÷
  // total — matching the stepper's ✓ marks, not raw position.
  const donePages = pages.reduce(
    (n, _p, i) => n + (i < currentPageIndex && !pagesWithGaps.has(i) ? 1 : 0),
    0,
  );
  const progressPct = Math.round((donePages / pages.length) * 100);

  // Desktop: brand + vertical stepper + progress in the rail; STEP header,
  // measured body, Prev/Next in the answer column. Narrow: brand band +
  // the top NamedStepper + sticky footer (today's layout).
  return (
    <FillDesktopFrame
      bodyScrollKey={currentPageIndex}
      brand={<FormBrandHeader variant="rail" />}
      rail={
        <VerticalStepper
          pages={pages}
          currentIndex={currentPageIndex}
          onJump={setCurrentPageIndex}
          pagesWithGaps={pagesWithGaps}
        />
      }
      progress={
        <div className="border-t border-[#edf0f4] px-[16px] py-[14px]">
          <div className="mb-[7px] flex justify-between text-[11px] font-bold text-[#6a7383]">
            <span>Progress</span>
            <span>{progressPct}%</span>
          </div>
          <div className="h-[6px] overflow-hidden rounded-[4px] bg-[#eef1f4]">
            <div
              className="h-full rounded-[4px] transition-[width]"
              style={{ width: `${progressPct}%`, backgroundColor: TINT }}
            />
          </div>
        </div>
      }
      header={
        <>
          <div className="mb-[4px] text-[11px] font-extrabold uppercase tracking-[0.07em] text-[#9aa3b0]">
            Step {currentPageIndex + 1} of {pages.length}
          </div>
          <div className="truncate text-[20px] font-extrabold tracking-[-0.01em] text-[#1f2a3d]">
            {currentName}
          </div>
        </>
      }
      body={body}
      footer={
        showFooter ? (
          <div className="flex items-center gap-[10px] px-[20px] py-[12px]">
            {footerButtons}
          </div>
        ) : null
      }
      narrow={
        <div className="flex min-h-full flex-1 flex-col">
          {/* Brand band + page bar pin together as one block at the top. */}
          <div className="sticky top-0 z-[6]" data-fill-pinned-top="">
            <FormBrandHeader variant="band" />
            {multi && (
              <NamedStepper
                pages={pages}
                currentIndex={currentPageIndex}
                onJump={setCurrentPageIndex}
                pagesWithGaps={pagesWithGaps}
              />
            )}
          </div>
          <div className="flex-1 space-y-4 px-[18px] pb-[22px] pt-[20px]">
            {body}
          </div>
          {showFooter && (
            <div
              className={cn(FILL_ACTION_BAR_CLASS, FILL_ACTION_BAR_NARROW_CLASS)}
              {...FILL_ACTION_BAR_ATTR}
            >
              {footerButtons}
            </div>
          )}
        </div>
      }
    />
  );
};

interface VerticalStepperProps {
  pages: DocumentElementType[];
  currentIndex: number;
  onJump: (index: number) => void;
  pagesWithGaps: Set<number>;
}

// The desktop rail's vertical stepper: a dot + label per page, connected by
// a 2px line. Done / current / upcoming states mirror the prototype; the
// whole row jumps to its page (same lock model as the dropdown — none extra).
const VerticalStepper: React.FC<VerticalStepperProps> = ({
  pages,
  currentIndex,
  onJump,
  pagesWithGaps,
}) => {
  const theme = useFormTheme();
  const accent = theme.accent;
  const accentContrast = theme.accentContrast;
  const ring = `color-mix(in srgb, ${accent} 14%, transparent)`;
  return (
    <div>
      <div className="px-[6px] pb-[10px] text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-[#9aa3b0]">
        Steps
      </div>
      {pages.map((page, index) => {
        const isCurrent = index === currentIndex;
        const done = index < currentIndex && !pagesWithGaps.has(index);
        const isLastRow = index === pages.length - 1;
        const hasGap = pagesWithGaps.has(index);
        return (
          <button
            key={page.id}
            type="button"
            onClick={() => onJump(index)}
            className="flex w-full items-start gap-[11px] text-left"
          >
            <div className="flex flex-none flex-col items-center self-stretch">
              <span
                className="inline-flex h-[24px] w-[24px] items-center justify-center rounded-full text-[11.5px] font-extrabold"
                style={
                  isCurrent
                    ? {
                        backgroundColor: accent,
                        color: accentContrast,
                        boxShadow: `0 0 0 4px ${ring}`,
                      }
                    : done
                      ? { backgroundColor: '#d8ecdf', color: '#1f8a4c' }
                      : { backgroundColor: '#f1f3f6', color: '#9aa3b0' }
                }
              >
                {done ? <Check className="h-[13px] w-[13px]" /> : index + 1}
              </span>
              {!isLastRow && (
                <span
                  className="my-[3px] w-[2px] flex-1"
                  style={{
                    minHeight: 16,
                    backgroundColor: done ? '#cfe6d7' : '#e3e7ec',
                  }}
                />
              )}
            </div>
            <div className="min-w-0 flex-1 pb-[14px] pt-[2px]">
              <div className="flex items-center gap-[6px]">
                <span
                  className={cn(
                    'truncate text-[12.5px]',
                    isCurrent
                      ? 'font-extrabold text-[#1f2a3d]'
                      : done
                        ? 'font-bold text-[#3a4252]'
                        : 'font-bold text-[#6a7383]',
                  )}
                >
                  {getPageName(page, index)}
                </span>
                {hasGap && !isCurrent && (
                  <span
                    className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#e5484d]"
                    title="Unanswered required field"
                  />
                )}
              </div>
              {isCurrent ? (
                <div
                  className="text-[10.5px] font-bold"
                  style={{ color: accent }}
                >
                  In progress
                </div>
              ) : done ? (
                <div className="text-[10.5px] text-[#9aa3b0]">Completed</div>
              ) : null}
            </div>
          </button>
        );
      })}
    </div>
  );
};

interface StepperProps {
  pages: DocumentElementType[];
  currentIndex: number;
  onJump: (index: number) => void;
  /** Page indices with a required-but-empty field (red dot in the menu). */
  pagesWithGaps: Set<number>;
}

// Named page stepper: "STEP n OF m" + page-name headline, a jump dropdown
// listing every page by name, and a page-progress bar. Sticky to the top of
// the scroll container. One pattern at every width.
const NamedStepper: React.FC<StepperProps> = ({
  pages,
  currentIndex,
  onJump,
  pagesWithGaps,
}) => {
  const [open, setOpen] = useState(false);
  // The popover renders in a portal outside the DocumentView's CSS-var scope,
  // so `var(--forms-accent)` (TINT) is dead there. Use the resolved theme
  // accent as a concrete colour instead (context still flows into portals).
  const theme = useFormTheme();
  const accent = theme.accent;
  const accentContrast = theme.accentContrast;
  const accentSoft = `color-mix(in srgb, ${accent} 12%, #fff)`;
  const total = pages.length;
  const stepNum = currentIndex + 1;
  const progressPct = Math.round((stepNum / total) * 100);
  const currentName = getPageName(pages[currentIndex], currentIndex);

  // Radix portals the popover to <body>, which sits outside the fill dialog's
  // scroll lock — so wheel events over the list are swallowed and it can't be
  // scrolled. Forward the wheel to the list ourselves (non-passive so we can
  // take over the default), only when it actually overflows.
  const attachWheelScroll = useCallback((node: HTMLDivElement | null) => {
    // Guard against a double-bind (StrictMode / re-invoked ref) which would
    // otherwise scroll twice per wheel tick.
    if (!node || (node as { __wheelBound?: boolean }).__wheelBound) return;
    (node as { __wheelBound?: boolean }).__wheelBound = true;
    node.addEventListener(
      'wheel',
      (e: WheelEvent) => {
        if (node.scrollHeight <= node.clientHeight) return;
        node.scrollTop += e.deltaY;
        e.preventDefault();
      },
      { passive: false },
    );
  }, []);

  return (
    <div className="z-[5] border-b border-[#edf0f4] bg-white px-[18px] pb-[13px] pt-[15px]">
      <div className="flex items-start gap-[12px]">
        <div className="min-w-0 flex-1">
          <div className="mb-[3px] text-[11px] font-extrabold uppercase tracking-[0.07em] text-[#9aa3b0]">
            Step {stepNum} of {total}
          </div>
          <div className="truncate text-[17px] font-extrabold tracking-[-0.01em] text-[#1f2a3d]">
            {currentName}
          </div>
        </div>

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex min-w-[132px] max-w-[60%] items-center gap-[8px] rounded-[9px] border border-[#dfe3e9] bg-white px-[10px] py-[8px]"
            >
              <span
                className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold"
                style={{ backgroundColor: TINT_SOFT, color: TINT }}
              >
                {stepNum}
              </span>
              <span className="flex-1 truncate text-left text-[12.5px] font-bold text-[#1f2a3d]">
                {currentName}
              </span>
              <ChevronDown className="h-[13px] w-[13px] shrink-0 text-[#9aa3b0]" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            ref={attachWheelScroll}
            align="end"
            // Show up to ~10 pages; scroll beyond. ≤10 fit with no scrollbar.
            className="max-h-[372px] w-[236px] overflow-y-auto rounded-[12px] p-[6px]"
          >
            {pages.map((page, index) => {
              const isCurrent = index === currentIndex;
              // A visited page only earns the check when nothing mandatory is
              // left to do on it; otherwise it keeps its number + the gap dot.
              const done = index < currentIndex && !pagesWithGaps.has(index);
              return (
                <button
                  key={page.id}
                  type="button"
                  onClick={() => {
                    onJump(index);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-[10px] rounded-[8px] px-[9px] py-[8px] text-left"
                  style={isCurrent ? { backgroundColor: accentSoft } : undefined}
                >
                  <span
                    className="inline-flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold"
                    style={
                      isCurrent
                        ? { backgroundColor: accent, color: accentContrast }
                        : done
                          ? { backgroundColor: '#d8ecdf', color: '#1f8a4c' }
                          : { backgroundColor: '#f1f3f6', color: '#9aa3b0' }
                    }
                  >
                    {done ? '✓' : index + 1}
                  </span>
                  <span
                    className={cn(
                      'flex-1 truncate text-[12.5px]',
                      isCurrent
                        ? 'font-extrabold text-[#1f2a3d]'
                        : done
                          ? 'font-semibold text-[#3a4252]'
                          : 'font-semibold text-[#6a7383]',
                    )}
                  >
                    {getPageName(page, index)}
                  </span>
                  {pagesWithGaps.has(index) && (
                    <span
                      className="h-[7px] w-[7px] shrink-0 rounded-full bg-[#e5484d]"
                      title="Unanswered required field"
                    />
                  )}
                  {isCurrent && (
                    <span
                      className="shrink-0 text-[10px] font-extrabold"
                      style={{ color: accent }}
                    >
                      NOW
                    </span>
                  )}
                </button>
              );
            })}
          </PopoverContent>
        </Popover>
      </div>

      <div className="mt-[12px] h-[6px] overflow-hidden rounded-[4px] bg-[#eef1f4]">
        <div
          className="h-full rounded-[4px] transition-[width]"
          style={{ width: `${progressPct}%`, backgroundColor: TINT }}
        />
      </div>
    </div>
  );
};

export default Pages;
